// src/pages/Dashboard.js
import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Briefcase, DollarSign, Activity, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../utils/api';
import { StatCard, Card, CardHeader, formatCurrency, formatPct, Badge } from '../components/ui';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/trading/dashboard').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 80, color: '#64748b' }}>Loading dashboard...</div>;

  const purchases = data?.purchases || {};
  const sells = data?.sells || {};
  const portfolio = data?.portfolio || {};
  const gainers = data?.top_gainers || [];
  const losers = data?.top_losers || [];

  const totalPnL = parseFloat(sells.total_pnl || 0);
  const unrealized = parseFloat(portfolio.unrealized_pnl || 0);
  const winRate = sells.total_sells > 0 ? ((sells.winning_trades / sells.total_sells) * 100).toFixed(1) : 0;

  const pieData = [
    { name: 'Invested', value: parseFloat(portfolio.total_holdings || 0) },
    { name: 'Gain', value: Math.max(parseFloat(portfolio.current_value || 0) - parseFloat(portfolio.total_holdings || 0), 0) },
  ];
  const COLORS = ['#3b82f6', '#10b981'];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.5px' }}>Dashboard</h1>
        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>Portfolio overview & performance summary</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Invested" value={formatCurrency(purchases.total_invested)} subvalue={`${purchases.total_trades || 0} trades`} icon={DollarSign} color="#3b82f6" />
        <StatCard label="Portfolio Value" value={formatCurrency(portfolio.current_value)} subvalue={formatPct((unrealized / (parseFloat(portfolio.total_holdings) || 1)) * 100)} icon={Briefcase} color="#8b5cf6" trend={unrealized >= 0 ? 'up' : 'down'} />
        <StatCard label="Realized P&L" value={formatCurrency(totalPnL)} subvalue={`Win rate: ${winRate}%`} icon={totalPnL >= 0 ? TrendingUp : TrendingDown} color={totalPnL >= 0 ? '#10b981' : '#ef4444'} trend={totalPnL >= 0 ? 'up' : 'down'} />
        <StatCard label="Unrealized P&L" value={formatCurrency(unrealized)} subvalue={`${sells.total_sells || 0} sells`} icon={Activity} color={unrealized >= 0 ? '#10b981' : '#ef4444'} trend={unrealized >= 0 ? 'up' : 'down'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Gainers */}
        <Card>
          <CardHeader title="Top Gainers" subtitle="Best performing stocks" />
          <div style={{ padding: '16px 24px' }}>
            {gainers.length === 0 ? <p style={{ color: '#94a3b8', fontSize: 13 }}>No sell transactions yet</p> :
              gainers.map((g, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < gainers.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{g.script_code}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{g.script_name}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#10b981', fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <ArrowUpRight size={14} />{formatCurrency(g.pnl)}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{g.trades} trade{g.trades > 1 ? 's' : ''}</div>
                  </div>
                </div>
              ))}
          </div>
        </Card>

        {/* Losers */}
        <Card>
          <CardHeader title="Top Losers" subtitle="Underperforming stocks" />
          <div style={{ padding: '16px 24px' }}>
            {losers.length === 0 ? <p style={{ color: '#94a3b8', fontSize: 13 }}>No losing trades yet</p> :
              losers.map((l, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < losers.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{l.script_code}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{l.script_name}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#ef4444', fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <ArrowDownRight size={14} />{formatCurrency(l.pnl)}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{l.trades} trade{l.trades > 1 ? 's' : ''}</div>
                  </div>
                </div>
              ))}
          </div>
        </Card>
      </div>

      {/* Portfolio Allocation */}
      {parseFloat(portfolio.total_holdings || 0) > 0 && (
        <Card>
          <CardHeader title="Portfolio Allocation" subtitle="Investment vs Market Value" />
          <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 40 }}>
            <PieChart width={200} height={200}>
              <Pie data={pieData} cx={100} cy={100} innerRadius={60} outerRadius={90} dataKey="value" paddingAngle={3}>
                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} />
            </PieChart>
            <div>
              {pieData.map((entry, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: COLORS[i] }} />
                  <span style={{ fontSize: 14, color: '#374151' }}>{entry.name}: <strong>{formatCurrency(entry.value)}</strong></span>
                </div>
              ))}
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 13, color: '#64748b' }}>Current Value</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: unrealized >= 0 ? '#10b981' : '#ef4444' }}>
                  {formatCurrency(portfolio.current_value)}
                </div>
                <div style={{ fontSize: 13, color: unrealized >= 0 ? '#10b981' : '#ef4444' }}>
                  {unrealized >= 0 ? '▲' : '▼'} {formatCurrency(Math.abs(unrealized))} ({formatPct((unrealized / (parseFloat(portfolio.total_holdings) || 1)) * 100)})
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
